import React, { useMemo, useState, useEffect } from "react";
import "./ManualCheckInModal.css";
import DateTimePicker from "./DateTimePicker";
import { shiftStaffEmployeeForAvatar } from "../utils/accountAvatarUrl";
import { formatLegacyScheduleDurationText } from "../utils/scheduleDurationUtils";
import {
  formatAppDateTime,
  formatAppTime,
  fromDatetimeLocalValue,
  parseDbDateTime,
  toDatetimeLocalValue,
  toMysqlUtcWallString,
} from "../utils/appDateTime";
import StaffAvatarCircle from "./StaffAvatarCircle";

export default function ManualCheckInModal({
  show,
  onClose,
  shift,
  onSave,
  onRoundDate,
  mode = "checkin",
  saving = false,
}) {
  const [attendanceDate, setAttendanceDate] = useState("");
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [roundingDate, setRoundingDate] = useState(false);
  const isCheckout = mode === "checkout";

  useEffect(() => {
    let cancelled = false;
    if (!show) {
      setRoundingDate(false);
      return () => {
        cancelled = true;
      };
    }

    const baseDate = isCheckout
      ? shift?.checkout_date || shift?.end_date || shift?.start_date
      : shift?.checkin_date || shift?.start_date;
    if (!baseDate) {
      setAttendanceDate("");
      return () => {
        cancelled = true;
      };
    }

    const parsedBaseDate = parseDbDateTime(baseDate) || new Date(baseDate);
    if (!parsedBaseDate || Number.isNaN(parsedBaseDate.getTime())) {
      setAttendanceDate(toDatetimeLocalValue(baseDate));
      return () => {
        cancelled = true;
      };
    }

    if (typeof onRoundDate !== "function") {
      setAttendanceDate(toDatetimeLocalValue(parsedBaseDate));
      return () => {
        cancelled = true;
      };
    }

    setRoundingDate(true);
    (async () => {
      try {
        const rounded = await onRoundDate(parsedBaseDate);
        if (!cancelled) {
          setAttendanceDate(toDatetimeLocalValue(rounded || parsedBaseDate));
        }
      } finally {
        if (!cancelled) {
          setRoundingDate(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isCheckout, shift, show]);

  const start = parseDbDateTime(shift?.start_date) || new Date();
  const end = parseDbDateTime(shift?.end_date) || new Date();

  const legacyDurationText = formatLegacyScheduleDurationText(shift);

  const staffName = shift?.account?.firstName
    ? `${shift.account.firstName} ${shift.account.lastName}`
    : shift?.outsourcedPayer?.payers?.name || "Unknown";

  const positionClient = `${shift?.position_name || ""} ${
    shift?.client_name || ""
  }`;

  const shiftIdentifier =
    shift?.id ||
    shift?.shift_id ||
    shift?.job_shift_id ||
    shift?.shiftId ||
    shift?.account_id ||
    "—";

  const attendanceStatus = useMemo(() => {
    const now = new Date();

    if (isCheckout) {
      if (shift?.checkout_date) {
        return {
          label: formatAppDateTime(shift.checkout_date),
          className: "pending",
        };
      }

      if (shift?.checkin_date) {
        return {
          label: "Checked in",
          className: "pending",
        };
      }
    }

    if (shift?.start_date) {
      const shiftStart = parseDbDateTime(shift.start_date);

      if (shiftStart && shiftStart > now) {
        return {
          label: "Future",
          className: "future",
        };
      }

      if (!shift?.checkin_date) {
        return {
          label: "Late",
          className: "late",
        };
      }
    }

    return {
      label:
        shift?.checkin_date && parseDbDateTime(shift.checkin_date)
          ? formatAppDateTime(shift.checkin_date)
          : "Pending signoff",
      className: "pending",
    };
  }, [isCheckout, shift]);

  const attendanceInstant = fromDatetimeLocalValue(attendanceDate);
  const attendanceValueText = attendanceInstant
    ? formatAppDateTime(attendanceInstant)
    : "";

  const pickerValue = attendanceInstant || (isCheckout ? end : start);

  const { accountId: avatarAccountId, employee: avatarEmployee } = useMemo(
    () => shiftStaffEmployeeForAvatar(shift),
    [shift],
  );

  const applyRoundedDate = async (date) => {
    if (!date) {
      setAttendanceDate("");
      return;
    }
    if (typeof onRoundDate !== "function") {
      setAttendanceDate(toDatetimeLocalValue(date));
      return;
    }
    setRoundingDate(true);
    try {
      const rounded = await onRoundDate(date);
      setAttendanceDate(toDatetimeLocalValue(rounded || date));
    } finally {
      setRoundingDate(false);
    }
  };

  if (!show) return null;

  return (
    <>
      <div className="modal-overlay manual-checkin-overlay" onClick={onClose}>
        <div
          className="manual-checkin-modal"
          onClick={(e) => e.stopPropagation()}
        >
          <h2 className="manual-checkin-title">
            {isCheckout ? "Manual check out" : "Manual check in"}
          </h2>

          <div className="header-row">
            <div className="manual-checkin-summary">
              <div className="id">ID: {shiftIdentifier}</div>

              <div className="position">{positionClient}</div>

              <div className="staff">{staffName}</div>

              <div className="schedule-label">Original Schedule</div>

              <div className="schedule-time">
                {formatAppDateTime(shift?.start_date)} - {formatAppTime(shift?.end_date)}
              </div>

              <div className="duration">
                {legacyDurationText}
              </div>

              <span className={`chip ${attendanceStatus.className}`}>
                {attendanceStatus.label}
              </span>
            </div>

            <StaffAvatarCircle
              accountId={avatarAccountId}
              employee={avatarEmployee}
              name={staffName}
              className="avatar w-14 h-14"
              textClassName="text-sm font-medium text-gray-700"
              emptyLabel="?"
            />
          </div>

          <div className="field">
            <label>{isCheckout ? "Check out*" : "Check in*"}</label>

            <button
              type="button"
              className="datetime-trigger"
              disabled={saving || roundingDate}
              onClick={() => setShowDatePicker(true)}
            >
              <span className="calendar-icon" aria-hidden="true">
                📅
              </span>
              <span className="datetime-value">{attendanceValueText}</span>
              <span
                className="clear-icon"
                aria-hidden="true"
                onClick={(e) => {
                  e.stopPropagation();
                  if (saving || roundingDate) return;
                  setAttendanceDate("");
                }}
              >
                ×
              </span>
            </button>
          </div>

          <div className="actions">
            <button
              className="save"
              disabled={saving || roundingDate}
              onClick={() => {
                const instant = fromDatetimeLocalValue(attendanceDate);
                onSave(instant ? toMysqlUtcWallString(instant) : attendanceDate);
              }}
            >
              {saving ? "SAVING..." : roundingDate ? "ROUNDING..." : "SAVE"}
            </button>

            <button className="cancel" disabled={saving || roundingDate} onClick={onClose}>
              CANCEL
            </button>
          </div>
        </div>
      </div>
      {showDatePicker && (
        <DateTimePicker
          key={attendanceDate || "default"}
          value={pickerValue}
          onChange={(date) => {
            if (date) setAttendanceDate(toDatetimeLocalValue(date));
          }}
          onDone={async (date) => {
            await applyRoundedDate(date);
            setShowDatePicker(false);
          }}
          onBack={() => setShowDatePicker(false)}
        />
      )}
    </>
  );
}
